import React, { useEffect, useRef, useState } from 'react'

import { Modal, Tab, Tabs } from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import AccountingExportSettings from './AccountingExportSettings'
import CalendarEventTypes from './CalendarEventTypes'
import ChangePswForm from './ChangePswForm'
import EmailSettingsForm from './EmailSettingsForm'
import MyInvoicesList from './MyInvoicesList'
import { SocialCredentials } from './SocialCredentials'
import UpdateProfileForm from './UpdateProfileForm'
import AppLayout from '../common/AppLayout'
import AppTitle from '../common/AppTitle'
import { useAuth } from '../common/Auth'
import NiceButton from '../common/NiceButton'
import { ClientFormWrapper } from '../common/styledComponents'
import { PriceTable } from '../home/PriceTable'
import axiosClient from '../service/axiosClient'
import ClientDTO from '../types/Client'
import { Package, PaymentPeriod } from '../types/gopay'

function UpdateProfilePage() {
    const {t} = useTranslation()
    const {user, logout, updateUser} = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const [currentSelectedPackage, setCurrentSelectedPackage] = useState<Package | undefined>(user?.selectedPackage)
    const [currentSelectedPeriod, setCurrentSelectedPeriod] = useState<PaymentPeriod | undefined>(user?.paymentPeriod)

    const isExpired = user?.selectedPackage === Package.EXPIRED
    const [activeKey, setActiveKey] = useState<string>(isExpired ? 'invoices' : 'profil')

    const [showModalClientsActivation, setShowModalClientsActivation] = useState(false)
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [selectedClientIds, setSelectedClientIds] = useState<number[]>([])
    const [maxClients, setMaxClients] = useState<number | null>(null)
    const newPackage = useRef<Package | undefined>(undefined)
    const newPeriod = useRef<PaymentPeriod | undefined>(undefined)
    const handleModalClientsClose = () => setShowModalClientsActivation(false)
    const handleModalClientsShow = () => setShowModalClientsActivation(true)

    // Set active tab based on the URL hash on initial load
    useEffect(() => {
        const currentHash = location.hash.replace('#', '')
        if (currentHash) {
            if (isExpired && !['invoices', 'packages'].includes(currentHash)) {
                setActiveKey('invoices')
            } else {
                setActiveKey(currentHash)
            }
        } else {
            if (isExpired) {
                setActiveKey('invoices')
            }
        }
    }, [location.hash, isExpired])

    // Update URL hash when the tab changes
    const handleSelect = (key: string | null) => {
        if (key) {
            setActiveKey(key)
            navigate(`#${key}`, {replace: true})
        }
    }

    function deleteAccount() {
        if (confirm(t('confirm_cancel_subscription'))) {
            axiosClient.delete('user/' + user?.id).then(async () => {
                toast.success(t('subscription_cancelled_success'))
                await updateUser()
            }).catch(() => {
                toast.error(t('subscription_cancel_error'))
            })
        }
    }

    function changePckg(clientsToActivate: number[], clientsToDeactivate: number[]) {
        console.log('changePckg', newPackage.current, newPeriod.current, clientsToActivate, clientsToDeactivate)
        axiosClient.post('gopay/package-change', {
            selectedPackage: newPackage.current,
            paymentPeriod: newPeriod.current,
            clientsToActivate: clientsToActivate,
            clientsToDeactivate: clientsToDeactivate
        }).then(response => {
            console.log('response', response)
            if (response.data && response.data.gwUrl) {
                const redirectUrl = response.data.gwUrl
                window.location.href = redirectUrl
            } else {
                setCurrentSelectedPackage(newPackage.current)
                setCurrentSelectedPeriod(newPeriod.current)
                toast.success(t('package_changed_success'))
            }
        }).catch(error => {
            console.log('error', error)
            toast.error(t('package_change_error'))
        })
    }

    function handleClientSelection() {
        // if (user) {
        //     changePckg(user.selectedPackage, user.paymentPeriod, selectedClientIds, clients)
        // }
        // clients to unlock = all clients - selectedClientIds
        const clientsToLock = clients.filter(client => !selectedClientIds.includes(client.id as number))
        changePckg(selectedClientIds, clientsToLock.map(client => client.id as number))
        handleModalClientsClose()
    }

    async function onPackageSelect(pckg: Package, period: PaymentPeriod) {
        setSelectedClientIds([])
        newPackage.current = pckg
        newPeriod.current = period
        console.log('selected package', pckg, period)

        axiosClient.get('client').then(response => {
            console.log('response', response)
            setClients(response.data)

            if (pckg !== Package.PREMIUM) {
                switch (pckg) {
                    case Package.BASIC:
                        if (response.data.length > 3) {
                            setMaxClients(3)
                            handleModalClientsShow()
                        } else {
                            changePckg(clients.map(client => client.id as number) ?? [], [])
                        }
                        break
                    case Package.MEDIUM:
                        if (response.data.length > 7) {
                            setMaxClients(7)
                            handleModalClientsShow()
                        } else {
                            changePckg(clients.map(client => client.id as number) ?? [], [])
                        }
                        break
                    // case Package.PREMIUM:
                    //     setMaxClients(null)
                    //     break
                }
            } else {
                changePckg(clients.map(client => client.id as number) ?? [], [])
            }
        }).catch(error => {
            console.log('error', error)
        })
    }

    const handleCheckboxChange = (clientId: number) => {
        setSelectedClientIds(prevSelectedClientIds => {
            if (prevSelectedClientIds.includes(clientId)) {
                return prevSelectedClientIds.filter(id => id !== clientId)
            } else if (maxClients === null || prevSelectedClientIds.length < maxClients) {
                return [...prevSelectedClientIds, clientId]
            }
            return prevSelectedClientIds
        })
    }
    console.log('^^^^user?.credentials', user?.credentials)
    return (
        <AppLayout>
            <Modal show={showModalClientsActivation} onHide={handleModalClientsClose}>
                <Modal.Title>
                    {maxClients ? t('modal_select_clients_max', {max: maxClients}) : t('modal_select_clients')}
                </Modal.Title>
                <Modal.Body>
                    {clients.map(client => (
                        <div className="form-group form-check" key={client.id}>
                            <input type="checkbox" id={`client-${client.id}`}
                                   className="form-check-input"
                                   disabled={maxClients !== null && selectedClientIds.length >= maxClients && !selectedClientIds.includes(client.id as number)}
                                   checked={selectedClientIds.includes(client.id as number)}
                                   onChange={() => {
                                       console.log('sme tu')
                                       handleCheckboxChange(client.id as number)
                                   }}
                                   name="client"/>
                            <label className="form-check-label" htmlFor={`client-${client.id}`}>
                                {client.firstName} {client.lastName}
                            </label>
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer>

                    <NiceButton
                        isLoading={false}
                        disabled={!(maxClients && selectedClientIds.length && selectedClientIds.length <= maxClients)}
                        onClick={handleClientSelection}>
                        {t('continue_button')}
                    </NiceButton>
                </Modal.Footer>
            </Modal>

            <AppTitle>{t('settings_title')}</AppTitle>

            <Tabs
                activeKey={activeKey} onSelect={handleSelect}
                id="setting-tabs"
            >
                {/* ZMENA: Nasledujúce taby sa zobrazia len ak NIE JE EXPIRED */}
                {!isExpired && (
                    <Tab eventKey="profil" title={t('tab_my_profile')}>
                        <ClientFormWrapper>
                            <div className="row d-flex">
                                <div className="col-md-8">
                                    <UpdateProfileForm/>
                                    <AccountingExportSettings/>
                                </div>
                            </div>
                        </ClientFormWrapper>
                    </Tab>
                )}

                {!isExpired && (
                    <Tab eventKey="eml" title={t('tab_email_settings')}>
                        <ClientFormWrapper>
                            <div className="row d-flex">
                                <div className="col-md-8">
                                    <EmailSettingsForm/>
                                </div>
                            </div>
                        </ClientFormWrapper>
                    </Tab>
                )}

                {!isExpired && (
                    <Tab eventKey="calendar-event-types" title={t('tab_calendar_event_types')}>
                        <ClientFormWrapper>
                            <div className="row d-flex">
                                <div className="col-md-12">
                                    <CalendarEventTypes/>
                                </div>
                            </div>
                        </ClientFormWrapper>
                    </Tab>
                )}

                {!isExpired && (
                    <Tab eventKey="change-psw" title={t('tab_change_password')}>
                        <ClientFormWrapper>
                            <div className="row d-flex">
                                <div className="col-md-8">
                                    <ChangePswForm/>
                                </div>
                            </div>
                        </ClientFormWrapper>
                    </Tab>
                )}

                {!isExpired && (
                    <Tab eventKey="socials" title={t('tab_social_credentials')}>
                        <SocialCredentials/>
                    </Tab>
                )}

                {/* Tieto taby sú viditeľné VŽDY (aj pri EXPIRED) */}
                <Tab eventKey="invoices" title={t('tab_invoices')}>
                    <ClientFormWrapper>
                        <MyInvoicesList/>
                    </ClientFormWrapper>
                </Tab>
                <Tab eventKey="packages" title={t('tab_packages')}>
                    <ClientFormWrapper>
                        <PriceTable defaultPackage={currentSelectedPackage} defaultPeriod={currentSelectedPeriod}
                                    onPackageSelect={onPackageSelect} hideTitle={true} hideFreePackage={false}/>
                    </ClientFormWrapper>
                </Tab>

                {!isExpired && (
                    <Tab eventKey="cancel-account" title={t('tab_cancel_subscription')}>
                        <ClientFormWrapper>
                            <div className="row d-flex">
                                <div className="col-md-8">
                                    <p>
                                        {t('cancel_subscription_text_1')}<br/><br/>

                                        {t('cancel_subscription_text_2')}<br/><br/>

                                        {t('cancel_subscription_text_3')}<br/>
                                        {t('cancel_subscription_text_4')}<br/><br/>

                                        {t('cancel_subscription_text_5')}<br/>
                                        {t('cancel_subscription_text_6')}<br/>
                                        <br/>
                                        {t('cancel_subscription_text_7')}<br/>
                                        {t('cancel_subscription_text_8')}
                                    </p>

                                    <NiceButton className={'dark-pink-color'} onClick={deleteAccount} type="button"
                                                isLoading={false}>
                                        {t('cancel_subscription_button')}
                                    </NiceButton>
                                </div>
                            </div>
                        </ClientFormWrapper>
                    </Tab>
                )}

            </Tabs>
        </AppLayout>
    )
}

export default UpdateProfilePage